import set from "@rill/set";
import { round } from "lodash";
import moment from "moment";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { FilterExerciseVideoList } from "../../../parts/modals/filter-exercise-video-list";
import { JWPlayerMultipleVideos } from "../../../parts/modals/jwplayer-multiple-videos";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import { formattedTarget } from "../../../utils/actuals";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../utils/video-modal-url";
import VideoList from "./VideoList";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const VideoListController = (app) => {
  app.get(
    "/video-list",
    set("heading", "Video List"),
    async ({ req, res, load, locals, momentTZ }) => {
      const { query = {} } = req;
      const modal = query.modal || {};
      const summary = query.summary || false;

      const athleteId = locals.athlete
        ? locals.athlete._id
        : locals.activeUser._id;

      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const to = query.toDate
        ? query.toDate
        : moment //today date but with T20:00:00Z format hour, to avoid timezones problems and for caching requests (if we request this endpoint and the seconds are changing the caching is not going to work, so we set a hardcore hour)
            .utc(moment())
            .set({
              hour: 20,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .format("YYYY-MM-DDTHH:mm:ss[Z]");

      const from = query.fromDate
        ? query.fromDate
        : moment //one year ago date but with T20:00:00Z format hour, to avoid timezones problems and for caching requests (if we request this endpoint and the seconds are changing the caching is not going to work, so we set a hardcore hour)
            .utc(moment().subtract(1, "year"))
            .set({
              hour: 20,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .format("YYYY-MM-DDTHH:mm:ss[Z]");

      await load("videoListMetrics", {
        page: query.pageToShow ? parseInt(query.pageToShow, 10) : 1,
        // counts Exercise docs, not video rows (a doc can hold several videos) —
        // 40 keeps each page's row count reasonably close to what "Show more"
        // implies, without paginating by row (which would cost a full-range
        // $unwind on every page instead of a cheap, doc-bounded $skip/$limit)
        limit: 40,
        sort: -1,
        proxyAthlete: athleteId,
        to,
        from,
        // filtering server side keeps the filter and the pagination consistent:
        // every page comes back already filtered, so "Load more" always adds rows
        exerciseFilters: query.exerciseFilters || undefined,
      });

      // the options come from the whole date range, not from the page the client
      // happens to have loaded, so expanding the range widens the filter too
      if (modal.type === "filter-exercise-videos-modal") {
        await load("videoListExerciseNames", {
          proxyAthlete: athleteId,
          to,
          from,
        });
      }

      if (query.threadId) {
        await load("comments", {
          threadId: query.threadId,
        });
      } else {
        locals.comments = [];
      }

      let comesFromCheckInSummary = false;
      // Required conditional because this could be undefined when refreshing
      if (Boolean(summary) && req?.original?.headers?.referer) {
        comesFromCheckInSummary =
          req.original.headers.referer.includes("check-in-summary");
      }
      if (query.showBack) comesFromCheckInSummary = true;

      // Build prev/next neighbors for watch-video-list modal
      let prevHref;
      let nextHref;
      if (modal.type === "watch-video-list") {
        const sequence = (locals.videoListMetrics?.exercises || []).map(
          (ex) => {
            const exSetIndex = Number.parseInt(ex.setIndex, 10);
            const exVideoIndex = Number.parseInt(ex.videoSet, 10);
            const seqOutcomeObj = ex.outcomes?.outcome || {};
            const seqExType = ex.type ?? ex.exercise?.type ?? "";
            const seqIsPowerlifting =
              seqExType === "primary" || seqExType === "secondary";
            const { units, intensityMeasure } = locals.activeUser;
            const seqPrecision = units === "metric" ? 2 : 1;
            const seqUserMetrics = units === "imperial" ? "lb" : "kg";
            const seqRawLoad = seqOutcomeObj.load;
            const seqLoad = seqRawLoad
              ? `${round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
              : 0;
            const seqTarget =
              seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                ? formattedTarget(
                    seqOutcomeObj,
                    seqIsPowerlifting,
                    ex.set?.percentRepMax || undefined,
                    units,
                    seqLoad,
                    intensityMeasure,
                    intensityMeasure !== "rpe",
                    true
                  ) || undefined
                : undefined;
            const seqSetNumber = ex.setNumber;
            return {
              key: `${ex.exerciseId}:${exSetIndex}:${exVideoIndex}`,
              href: buildVideoModalUrl({
                modalType: "watch-video-list",
                video: {
                  videoId: ex.videoId,
                  videoOriginal: ex.videoOriginal,
                  reviewedByTheCoach: ex.reviewedByTheCoach,
                  uploaderId: ex.uploaderId || athleteId,
                  thread: ex.thread,
                },
                exerciseToEdit: ex.exerciseId,
                setIndex: exSetIndex,
                videoIndex: exVideoIndex,
                utcDate: ex.utcDate,
                primary: ex.primary ?? ex.exercise?.primary,
                secondary: ex.secondary ?? ex.exercise?.secondary,
                type: ex.type ?? ex.exercise?.type,
                movement: ex.movement ?? ex.exercise?.movement,
                setNumber: seqSetNumber,
                setTarget: seqTarget,
                setComment: ex.comments || undefined,
              }),
            };
          }
        );
        const currentKey = `${query.exerciseToEdit}:${Number.parseInt(query.setIndex, 10)}:${Number.parseInt(query.videoIndex, 10)}`;
        ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
      }

      locals.modal = {
        "watch-video-list": (
          <JWPlayerVideoModal
            mediaId={query.mediaId}
            originalId={query.originalId}
            athleteId={query.mediaAthleteId}
            exerciseToEdit={query.exerciseToEdit}
            reviewedByTheCoach={query.reviewedByTheCoach}
            setIndex={query.setIndex}
            videoIndex={query.videoIndex}
            threadId={query.threadId}
            locals={locals}
            utcDate={query.utcDate}
            primary={query.primary}
            secondary={query.secondary}
            type={query.type}
            movement={query.movement}
            prevHref={prevHref}
            nextHref={nextHref}
            setNumber={query.setNumber}
            setTarget={query.setTarget}
            setComment={query.setComment}
          />
        ),
        "watch-multiple-videos-modal": (
          <JWPlayerMultipleVideos
            firstMedia={query.firstMedia}
            secondMedia={query.secondMedia}
            athlete={query.athlete}
            locals={locals}
          />
        ),
        "filter-exercise-videos-modal": (
          <FilterExerciseVideoList
            exercises={locals.videoListExerciseNames?.names || []}
            exerciseFilters={query.exerciseFilters}
          />
        ),
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = (
        <VideoList
          query={query}
          locals={locals}
          momentTZ={momentTZ}
          comesFromCheckInSummary={comesFromCheckInSummary}
        />
      );
    }
  );
};

export default VideoListController;
